import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '@/lib/api';
/* Public mobile-capture page. Reached by scanning the QR code displayed
 * during desktop sign-and-approve flow. The URL embeds a short-lived
 * token tied to one SignatureCaptureSession on the backend; submitting
 * the canvas marks the session captured and the desktop poller picks
 * it up. */
export function MobileSignPage() {
    const { token } = useParams();
    const info = useQuery({
        queryKey: ['sign-info', token],
        queryFn: async () => (await api.get(`/signing/c/${token}/info/`)).data,
        retry: false,
    });
    const canvasRef = useRef(null);
    const drawing = useRef(false);
    const [hasInk, setHasInk] = useState(false);
    const [done, setDone] = useState(false);
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas)
            return;
        // Touch-first canvas: scale to device pixel ratio so strokes stay
        // sharp on mobile retina screens.
        const dpr = window.devicePixelRatio || 1;
        const rect = canvas.getBoundingClientRect();
        canvas.width = rect.width * dpr;
        canvas.height = rect.height * dpr;
        const ctx = canvas.getContext('2d');
        if (!ctx)
            return;
        ctx.scale(dpr, dpr);
        ctx.lineWidth = 2.5;
        ctx.lineCap = 'round';
        ctx.strokeStyle = '#111';
        function pos(e) {
            const r = canvas.getBoundingClientRect();
            return { x: e.clientX - r.left, y: e.clientY - r.top };
        }
        function down(e) {
            e.preventDefault();
            drawing.current = true;
            const { x, y } = pos(e);
            ctx.beginPath();
            ctx.moveTo(x, y);
        }
        function move(e) {
            if (!drawing.current)
                return;
            e.preventDefault();
            const { x, y } = pos(e);
            ctx.lineTo(x, y);
            ctx.stroke();
            setHasInk(true);
        }
        function up() { drawing.current = false; }
        canvas.addEventListener('pointerdown', down);
        canvas.addEventListener('pointermove', move);
        canvas.addEventListener('pointerup', up);
        canvas.addEventListener('pointerleave', up);
        canvas.addEventListener('pointercancel', up);
        return () => {
            canvas.removeEventListener('pointerdown', down);
            canvas.removeEventListener('pointermove', move);
            canvas.removeEventListener('pointerup', up);
            canvas.removeEventListener('pointerleave', up);
            canvas.removeEventListener('pointercancel', up);
        };
    }, []);
    function clearCanvas() {
        const c = canvasRef.current;
        if (!c)
            return;
        const ctx = c.getContext('2d');
        if (!ctx)
            return;
        ctx.save();
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, c.width, c.height);
        ctx.restore();
        setHasInk(false);
    }
    const submit = useMutation({
        mutationFn: async () => {
            const c = canvasRef.current;
            if (!c)
                throw new Error('no canvas');
            const dataUrl = c.toDataURL('image/png');
            return (await api.post(`/signing/c/${token}/`, { signature_data_url: dataUrl })).data;
        },
        onSuccess: () => setDone(true),
    });
    if (info.isLoading) {
        return _jsx("p", { className: "p-8 text-center text-sm text-muted-foreground", children: "Loading\u2026" });
    }
    if (info.isError) {
        return (_jsxs("div", { className: "mx-auto max-w-md p-6 text-center", children: [_jsx("h1", { className: "text-xl font-semibold", children: "Link expired" }), _jsx("p", { className: "mt-2 text-sm text-muted-foreground", children: "The signature link has already been used or has timed out. Ask the requester to start a new session." })] }));
    }
    if (done) {
        return (_jsxs("div", { className: "mx-auto max-w-md p-6 text-center", children: [_jsx("h1", { className: "text-xl font-semibold text-emerald-700", children: "\u2713 Signature captured" }), _jsx("p", { className: "mt-2 text-sm text-muted-foreground", children: "You can close this tab \u2014 the document will be signed automatically on the desktop." })] }));
    }
    return (_jsxs("div", { className: "mx-auto max-w-md space-y-4 p-4", children: [_jsxs("header", { className: "space-y-1", children: [_jsx("h1", { className: "text-lg font-semibold", children: "Sign on phone" }), _jsxs("p", { className: "text-xs text-muted-foreground", children: [info.data?.requested_by, " is asking for your signature", info.data?.purpose ? ` (${info.data.purpose})` : '', "."] })] }), _jsx("canvas", { ref: canvasRef, style: { touchAction: 'none' }, className: "h-64 w-full rounded-lg border border-input bg-white shadow-inner" }), _jsxs("div", { className: "flex items-center justify-between text-xs", children: [_jsx("button", { type: "button", onClick: clearCanvas, className: "text-muted-foreground underline", children: "Clear" }), _jsx("span", { className: "text-muted-foreground", children: "Use your finger or pen." })] }), _jsx("button", { type: "button", onClick: () => submit.mutate(), disabled: !hasInk || submit.isPending, className: "w-full rounded-md bg-primary px-4 py-3 text-base font-medium text-primary-foreground disabled:opacity-50", children: submit.isPending ? 'Sending…' : 'Submit signature' })] }));
}
